<template>
  <div class="google-map-container">
    <el-collapse
      v-model="activeCollapseName"
      class="col-sm-12"
      accordion
    >
      <el-collapse-item
        :name="googleMapCollapseName"
        :title="collapseTitle"
      >
        <!-- Street View Map -->
        <div
          v-if="isSideBySide"
          class="location-map street-view-map"
        >
          <GoogleMap
            v-show="isStreetViewAvailable"
            ref="streetViewMap"
            class="google-map"
            gesture-handling="greedy"
            :center="center"
            :zoom="zoom"
            :map-type-id="mapTypeId"
            :scrollwheel="true"
            :street-view-control="false"
            :api-key="util.googleApiKey"
          >
            <GoogleMarker
              v-for="marker of availableMarkers"
              :key="`${Date.now()}-${marker.index}-marker`"
              :options="{
                label: {
                  text: `${marker.label || ' '}`,
                  color: '#000',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  marginTop: '-10px',
                  margin: '-10px 0 0',
                  x: '200',
                  y: '100',
                },
                position: marker.position,
                icon: marker.icon,
                draggable: marker.isDraggable === false ? false : isDraggable,
                zIndex: marker.zIndex,
              }"
              @dragend="onDragMarker($event.latLng, marker.index)"
            />
          </GoogleMap>
          <div
            v-show="!isStreetViewAvailable"
            class="no-street-view"
          >
            No Street View Map Available
          </div>
          <div
            class="compass-container"
            @click="resetStreetViewHeading"
          >
            <compass :heading="streetViewHeading" />
          </div>
        </div>
        <!-- Main Map -->
        <div class="location-map">
          <GoogleMap
            ref="mainMap"
            class="google-map"
            gesture-handling="greedy"
            :center="center"
            :zoom="zoom"
            :map-type-id="mapTypeId"
            :scrollwheel="true"
            :keyboard-shortcuts="false"
            :api-key="util.googleApiKey"
          >
            <template v-if="polylines.length">
              <GooglePolyline
                v-for="(polyline, index) in polylines"
                :key="`polyline-${index}`"
                :options="{
                  path: polyline.path,
                  strokeColor: polyline.strokeColor || '#F00',
                  strokeOpacity: polyline.strokeOpacity,
                  strokeWeight: polyline.strokeWeight,
                }"
              />
            </template>
            <GooglePolyline
              v-else-if="paths?.length > 0"
              :options="{
                path: paths as google.maps.LatLngLiteral[],
                strokeColor: '#F00',
              }"
            />
            <GooglePolygon
              v-if="polygons?.length > 0"
              :options="{
                paths: polygons,
                strokeColor: '#F00',
              }"
            />
            <GoogleCircle
              v-for="marker in availableCircles"
              :key="marker.index + '-circle'"
              :options="{
                center: locationBound[marker.index].getCenter(),
                radius: radius > 0 ? radius : googleRadius,
                fillOpacity: 0,
                strokeColor: '#FF0000',
                strokeOpacity: 0.5,
                strokeWeight: 2,
              }"
            />
            <GoogleMarker
              v-for="marker of availableMarkers"
              :key="`${Date.now()}-${marker.index}-marker`"
              :options="{
                label: {
                  text: `${marker.label || ' '}`,
                  color: '#000',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  marginTop: '-10px',
                  margin: '-10px 0 0',
                  x: '200',
                  y: '100',
                },
                position: marker.position,
                icon: marker.icon,
                draggable: marker.isDraggable === false ? false : isDraggable,
                zIndex: marker.zIndex,
              }"
              @dragend="onDragMarker($event.latLng, marker.index)"
              @click="(event: any) => onClickedMarker(event.latLng, marker)"
            />
            <GoogleCustomMarker
              v-for="customMarker of customMarkers"
              :key="`${customMarker.key}-pin`"
              :options="{
                position: customMarker.position,
                anchorPoint: 'BOTTOM_CENTER',
                offsetY: 7,
                zIndex: customMarker.zIndex,
              }"
            >
              <div class="map-pin">
                <span
                  v-if="customMarker.isLabelVisible || customMarker.isHighlighted"
                  class="map-pin__label"
                >{{ customMarker.label }}</span>
                <span
                  class="map-pin__dot"
                  :class="{
                    'map-pin__dot--ringed': customMarker.hasRing,
                    'map-pin__dot--highlighted': customMarker.isHighlighted,
                  }"
                  :style="{ backgroundColor: customMarker.color }"
                />
              </div>
            </GoogleCustomMarker>
          </GoogleMap>
          <div
            class="compass-container"
            @click="resetMainMapHeading"
          >
            <compass :heading="mainMapHeading" />
          </div>
        </div>

        <div
          v-if="description"
          class="description"
        >
          {{ description }}
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, PropType, Ref, ref, watch } from 'vue';
import {
  Circle as GoogleCircle,
  CustomMarker as GoogleCustomMarker,
  GoogleMap,
  Marker as GoogleMarker,
  Polyline as GooglePolyline,
  Polygon as GooglePolygon,
} from 'vue3-google-map';
import delay from 'yoctodelay';

import util from '@/Utils/util';
import { GeoLocationType, type MapMarker, type MapCustomMarker } from '@/models/address';
import { defaultMapCenter } from '@/constants/address';
import { googleRadius } from '@/constants/app';
import compass from '@/components/UIComponents/Compass.vue';

type MapPolyline = {
  path: google.maps.LatLngLiteral[];
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
};
type LocationBoundType = google.maps.LatLngBounds | null;
const googleMapCollapseName = 'google-map-collapse';

const props = defineProps({
  zoom: { type: Number, default: 14 },
  isDraggable: { type: Boolean, default: true },
  mapTypeId: { type: String, default: 'roadmap' },
  radius: { type: Number, default: googleRadius },
  markers: { type: Array as PropType<MapMarker[]>, default: () => [] },
  customMarkers: { type: Array as PropType<MapCustomMarker[]>, default: () => [] },
  mapCenter: { type: Object as PropType<GeoLocationType>, default: () => defaultMapCenter },
  paths: { type: Array<any>, default: () => [] },
  polygons: { type: Array<any>, default: () => [] },
  polylines: { type: Array as PropType<MapPolyline[]>, default: () => [] },
  description: { type: String, default: '' },
  collapseTitle: { type: String, default: 'Location Map' },
  isOpenDefault: { type: Boolean, default: false },
  isSideBySide: { type: Boolean, default: false },
});

const emit = defineEmits(['change', 'drag-pin', 'open-collapse', 'click-marker']);
const activeCollapseName = ref('');
const center: Ref<GeoLocationType> = ref({ lat: 37.42248, lng: -122.08547 });
const locationBound: Ref<LocationBoundType[]> = ref([]);
const isStreetViewAvailable = ref(true);
const streetViewMap: Ref<any> = ref({});
const mainMap: Ref<any> = ref(null);

const streetViewHeading = ref(0);
const mainMapHeading = ref(0);

const availableMarkers = computed(() => props.markers.filter((marker: any) => marker.isShow) || []);
const availableCircles = computed(() => props.markers.filter((marker: any) => marker.isShow && locationBound.value[marker.index]) || []);

const onClickedMarker = (latLng: google.maps.LatLng, marker: MapMarker) => {
  if (!marker.isClickable) {
    return;
  }

  const latLong: google.maps.LatLngLiteral = { lat: latLng.lat(), lng: latLng.lng() };
  updateStreetMapView(latLong.lat, latLong.lng);
  emit('click-marker', marker.index);
};

const onDragMarker = async (latLng: google.maps.LatLng, index: number) => {
  const latLong: google.maps.LatLngLiteral = { lat: latLng.lat(), lng: latLng.lng() };
  const isContain = locationBound.value[index]?.contains(latLng);
  if (isContain) {
    emit('change', latLong.lat, latLong.lng, index);
    emit('drag-pin', index);
  } else {
    props.markers[index].isShow = false;
    await delay(100);
    props.markers[index].isShow = true;
  }
  updateStreetMapView(latLong.lat, latLong.lng);
};

const checkStreetViewAvailable = async (lat: number, lng: number) => {
  const streetViewService = new google.maps.StreetViewService();
  try {
    await streetViewService.getPanorama({ location: { lat, lng } });
    return true;
  } catch {
    return false;
  }
};

const updateStreetMapView = async (lat: number, lng: number) => {
  if (!props.isSideBySide) return;
  await nextTick();
  isStreetViewAvailable.value = await checkStreetViewAvailable(lat, lng);
  if (!isStreetViewAvailable.value) return;
  const streetViewMapComp: google.maps.StreetViewPanorama = streetViewMap.value?.map?.getStreetView();
  if (!streetViewMapComp) return;
  streetViewMapComp.setPosition({ lat, lng });
  streetViewMapComp.setVisible(true);
  streetViewMapComp.setOptions({
    addressControl: false,
    enableCloseButton: false,
    fullscreenControl: false,
    disableDefaultUI: false,
    zoomControl: false,
  });
  google.maps.event.addListener(streetViewMapComp, 'pov_changed', () => {
    const pov = streetViewMapComp.getPov();
    streetViewHeading.value = pov.heading || 0;
  });
};

const getLocationBound = () => {
  for (const marker of props.markers) {
    if (marker.isShow && marker.isDraggable) {
      const lat = (marker.initialPosition && marker.initialPosition.lat) || marker.position.lat;
      const lng = (marker.initialPosition && marker.initialPosition.lng) || marker.position.lng;
      const circle = new google.maps.Circle({
        center: { lat, lng },
        radius: props.radius > 0 ? props.radius : googleRadius,
      });
      locationBound.value[marker.index] = circle.getBounds();
    }
  }
};

watch(
  () => props.mapCenter,
  (value: GeoLocationType) => {
    center.value = value;
    updateStreetMapView(value.lat || defaultMapCenter.lat, value.lng || defaultMapCenter.lng);
  },
  { deep: true, immediate: true },
);

watch(
  () => props.markers,
  () => getLocationBound(),
  { deep: true, immediate: true },
);
watch(
  () => props.radius,
  () => getLocationBound(),
);
watch(
  () => props.isOpenDefault,
  (value: boolean) => {
    activeCollapseName.value = value ? googleMapCollapseName : '';
  },
  { immediate: true },
);
watch(activeCollapseName, (value: string) => {
  emit('open-collapse', !!value);
});

onMounted(() => {
  const pollInterval = setInterval(() => {
    if (mainMap.value && mainMap.value.map) {
      clearInterval(pollInterval);
      const mapInstance = mainMap.value.map;
      mapInstance.setTilt(0);
      mapInstance.setHeading(0);

      google.maps.event.addListener(mapInstance, 'heading_changed', () => {
        mainMapHeading.value = mapInstance.getHeading() || 0;
      });

      google.maps.event.addListener(mapInstance, 'tilt_changed', () => {
        mainMapHeading.value = mapInstance.getHeading() || 0;
      });

      google.maps.event.addListener(mapInstance, 'idle', () => {
        mainMapHeading.value = mapInstance.getHeading() || 0;
      });
    }
  }, 1000);
});

const resetMainMapHeading = () => {
  if (mainMap.value && mainMap.value.map) {
    mainMap.value.map.setTilt(0);
    mainMap.value.map.setHeading(0);
  }
};

const resetStreetViewHeading = () => {
  const streetViewMapComp: google.maps.StreetViewPanorama = streetViewMap.value?.map?.getStreetView();
  if (streetViewMapComp) {
    const currentPov = streetViewMapComp.getPov();
    streetViewMapComp.setPov({ heading: 0, pitch: currentPov.pitch });
  }
};
</script>
<style lang="scss">
.google-map-container {
  margin-bottom: 20px;
  position: relative;

  @media (max-width: 576px) {
    margin: 0;
  }

  .el-collapse {
    padding: 0;
    width: 100%;

    .el-collapse-item {
      background-color: #f1f1f1;
      border: 1px solid #7ac29a;

      .el-collapse-item__header {
        background-color: #f1f1f1;
        color: #751f1f;
        height: 35px;
        line-height: 35px;
        padding-left: 10px;
        margin-bottom: 5px;
        border-bottom: 1px solid #bc8112;
        border-radius: 0;
        box-shadow: none;

        &.is-active {
          border-bottom: 1px solid #bc8112;
        }
      }

      .el-collapse-item__content {
        background-color: #f1f1f1;
        padding-bottom: 0;
      }

      .el-collapse-item__wrap {
        border-radius: 0;
      }
    }
  }

  .location-map {
    position: relative;
  }

  .street-view-map {
    .no-street-view {
      height: 200px;
      background: #000;
      font-size: 30px;
      color: #fff;
      font-weight: bold;
      line-height: 200px;
      text-align: center;
    }

    .google-map {
      border-bottom: 0;
    }
  }

  .google-map {
    width: 100%;
    height: 400px;
    border: 2px solid #bc8111;
    z-index: 0;

    button {
      min-width: unset;
      width: 40px !important;
    }

    .gmnoprint.gm-style-cc,
    .gm-style-cc:last-child {
      display: none;
    }

    a[title='Report errors in the road map or imagery to Google'],
    a[title='Report problems with Street View imagery to Google'] {
      display: none !important;
    }

    .google-map {
      border: 0;
      height: 100%;
    }
  }

  .compass-container {
    position: absolute;
    top: 50px;
    right: 0;
    z-index: 1000;
    cursor: pointer;
  }

  .description {
    padding: 5px 10px;
    color: #333;
    font-weight: 600;
    font-size: 14px;
  }

  .map-pin {
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;

    &__label {
      position: relative;
      padding: 6px 12px;
      margin-bottom: 8px;
      background-color: #fff;
      border: 1px solid #e4e7ed;
      border-radius: 8px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
      font-size: 14px;
      font-weight: 700;
      line-height: 1;
      white-space: nowrap;
      color: #252422;

      &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        width: 10px;
        height: 10px;
        background-color: #fff;
        border-right: 1px solid #e4e7ed;
        border-bottom: 1px solid #e4e7ed;
        transform: translate(-50%, -6px) rotate(45deg);
      }
    }

    &__dot {
      box-sizing: border-box;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
      transition: transform 0.15s ease-out;

      &--ringed {
        width: 24px;
        height: 24px;
        border: 5px solid #fff;
      }

      &--highlighted {
        transform: scale(1.35);
        box-shadow: 0 0 0 5px rgba(96, 80, 197, 0.35), 0 1px 3px rgba(0, 0, 0, 0.35);
      }
    }
  }
}
</style>
